import {DatePipe} from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  signal
} from '@angular/core';
import {NavigationExtras, Router} from '@angular/router';
import {
  NodePath,
  REF_MODEL_STORE,
  Refmodel,
  RefModelNode,
  RefModelStoreService,
  SalesForceData,
  SYNC_STATUS
} from '@q9elements/ui-kit/common';
import {RefModelSidebarService} from '@q9elements/ui-kit/refmodels';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ManageCurrentSfProcessService} from '../../../services/manage-current-sf-process.service';
import {RefModelService} from '../../../services/ref-model.service';

import {FirebaseSubscriptionsService} from '../../services/firebase-subscriptions.service';
import {TreePaginationService} from '../../services/tree-pagination.service';

@Component({
  selector: 'app-node-path',
  templateUrl: './node-path.component.html',
  styleUrls: ['./node-path.component.scss']
})
export class NodePathComponent implements OnInit, OnDestroy {
  @Input() nodePath: Array<NodePath>;

  private syncProgress: any;
  private destroySubject: Subject<void> = new Subject();
  private injector = inject(Injector);

  public refModel: Refmodel | SalesForceData;
  public fieldImpact: any;
  public node: RefModelNode;
  public syncStatus = signal('');

  constructor(
    private router: Router,
    private treePaginationService: TreePaginationService,
    private rmeStore: RefModelStoreService,
    private changeDetectorRef: ChangeDetectorRef,
    private datePipe: DatePipe,
    private refModelService: RefModelService,
    private firebaseSubscriptionsService: FirebaseSubscriptionsService,
    private manageCurrentProcessService: ManageCurrentSfProcessService,
    private refModelSidebarService: RefModelSidebarService
  ) {}

  ngOnInit() {
    this.refModel = this.rmeStore.refModel;
    this.syncStatus.set(this.refModel.syncStatus);

    effect(
      () => {
        const refModel = this.refModelSidebarService.refModelMeta();
        if (refModel) {
          this.refModel = refModel;
          this.syncStatus.set(refModel.syncStatus);

          if (refModel.syncStatus !== SYNC_STATUS.IN_PROGRESS) {
            this.firebaseSubscriptionsService.subscribeToSyncStatus(
              refModel._id,
              this.onSyncStatusUpdate.bind(this)
            );
          }

          if (
            refModel.syncStatus === SYNC_STATUS.IN_PROGRESS &&
            refModel.lastSyncUUID &&
            !this.syncProgress
          ) {
            this.firebaseSubscriptionsService.subscribeToSyncProgress(
              refModel._id,
              refModel.lastSyncUUID,
              this.onSyncProgressUpdate.bind(this)
            );
          }

          if (refModel.syncStatus === SYNC_STATUS.FAILED) {
            this.syncStatus.set(SYNC_STATUS.FAILED);
            this.firebaseSubscriptionsService.subscribeToSyncStatus(
              refModel._id,
              this.onSyncStatusUpdate.bind(this)
            );
          }
        }
      },
      {allowSignalWrites: true, injector: this.injector}
    );

    this.rmeStore
      .select(REF_MODEL_STORE.SELECTED_NODE)
      .pipe(takeUntil(this.destroySubject))
      .subscribe((node: RefModelNode) => {
        if (node) {
          this.node = Object.assign({}, node);
        }
      });
  }

  private onSyncProgressUpdate(snapshot) {
    const progress = snapshot.val();
    if (
      progress &&
      (progress.status === 'terminated' ||
        progress.status === 'failed' ||
        progress.currentStep.name === 'finish_sync' ||
        progress.currentStep.name === 'sync_finished')
    ) {
      this.manageCurrentProcessService.sfProcessStop('sync');
      return this.onSyncEnd();
    }
    this.manageCurrentProcessService.sfProcessStart('sync');
    this.syncProgress = progress;
    this.changeDetectorRef.detectChanges();
  }

  private onSyncEnd() {
    this.syncProgress = null;
    this.firebaseSubscriptionsService.unsubscribeFromSyncProgress(
      this.refModel._id,
      this.refModel.lastSyncUUID
    );
    this.refModelService.getRefModelMeta(this.refModel._id).subscribe(res => {
      this.rmeStore.set<Refmodel>(REF_MODEL_STORE.REF_MODEL, res as Refmodel);
      this.changeDetectorRef.detectChanges();
    });
  }

  private onSyncStatusUpdate(snapshot) {
    const status = snapshot.val();
    if (status === SYNC_STATUS.IN_PROGRESS) {
      this.refModelService.getRefModelMeta(this.refModel._id).subscribe(res => {
        if (res.syncStatus !== this.refModel.syncStatus) {
          this.rmeStore.set<Refmodel>(REF_MODEL_STORE.REF_MODEL, res as Refmodel);
          this.changeDetectorRef.detectChanges();
        }
      });
    }
  }

  makeNodeRoot(nodeId?: string) {
    const navigationExtras: NavigationExtras = nodeId ? {queryParams: {node: nodeId}} : {};

    this.treePaginationService.setPageIndex(0);
    this.router.navigate(['/refmodel', this.refModel._id], navigationExtras).then(() => {});
  }

  getSyncMessage(status: string) {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const lastSyncStarted = this.datePipe.transform(
      this.refModel.lastSyncStarted,
      "MMMM d, y 'at' HH:mm",
      userTimezone
    );
    switch (status) {
      case 'never':
        return 'Never synced';
      case 'starting':
        return '0%: Starting Sync';
      case 'inProgress':
        return this.syncProgress
          ? `${this.syncProgress.progress}% : ${this.syncProgress.currentStep.displayName}`
          : '';
      case 'updated':
        return `Last synced on: ${lastSyncStarted}`;
      case 'failed':
        return `Sync failed on: ${lastSyncStarted}`;
      default:
        return lastSyncStarted;
    }
  }

  ngOnDestroy() {
    if (this.syncProgress) {
      this.firebaseSubscriptionsService.unsubscribeFromSyncProgress(
        this.refModel._id,
        this.refModel.lastSyncUUID
      );
    }
    this.firebaseSubscriptionsService.unsubscribeFromSyncStatus(this.refModel._id);
    this.destroySubject.next();
  }
}
